import store from '@/store';
import cloneDeep from 'lodash/cloneDeep';
import { getDefaultSearcherContentTypesForSite } from '@/modules/searcher/config/default-seracher-content-types';
import get from 'lodash/get';

export function encodeSortString(sort) {
  return [Object.keys(sort)
    .shift(), Object.values(sort)
    .shift()].join(',');
}

export function decodeSortString(sort) {
  const decodedSort = sort.split(',');
  if (decodedSort.length === 2) {
    return { [decodedSort[0]]: decodedSort[1] };
  }
  return null;
}

/**
 * Añade el criterio de ordenación por defecto a la ruta, si todavía no hay uno establecido.
 *
 * @param {{}} location
 * @param {string} entity
 * @return {boolean} Si la ruta original ha sido modificada o no.
 */
const addDefaultSortToRoute = (location, entity) => {
  if (location.query.sort) {
    return false;
  }
  const defaultSort = get(store.getters.searcher, `${entity}.sort.default`);
  if (!defaultSort) {
    return false;
  }

  location.query.sort = encodeSortString(defaultSort);
  return true;
};

/**
 * Añade el número de resultados por defecto a la ruta, si todavía no hay uno establecido.
 *
 * @param {{}} location
 * @param {string} entity
 * @return {boolean} Si la ruta original ha sido modificada o no.
 */
const addDefaultLimitToRoute = (location, entity) => {
  if (location.query.limit) {
    return false;
  }
  const searchLimitConfig = store.getters.searchLimitConfigForEntity(entity);
  location.query.limit = searchLimitConfig.default;

  return true;
};

/**
 * Añade el filtro de tipo de contenido a las rutas del buscador de contenidos, si todavía no hay uno establecido.
 *
 * @param {{}} location
 * @param {string} entity
 * @return {boolean} Si la ruta original ha sido modificada o no.
 */
const addTypeFilterToContentRoute = (location, entity) => {
  if (location.query.type || entity !== 'content') {
    return false;
  }

  location.query.type = getDefaultSearcherContentTypesForSite(store.state.site.idAlias);
  return true;
};

/**
 * Añade el filtro por site si todavía no existe.
 *
 * @param {{}} location
 * @return {boolean} Si la ruta original ha sido modificada o no.
 */
const addSiteIdFilterToRoute = (location) => {
  if (location.query.siteId) {
    return false;
  }
  location.query.siteId = store.getters.availableSites
    .find(item => item.idAlias === store.state.site.idAlias).id;
  return true;
};

/**
 * Añade parámetros por defecto a las rutas del buscador.
 * Devuelve un objeto con dos propiedades:
 *   - `modified` indicando si la ruta original ha sido modificada porque se ha añadido algún parámetro, y
 *   - `location` la ruta resultante después de haber añadido los parámetros por defecto.
 *
 * @param {{}} storeLocation
 * @return {{modified: boolean, location: {}}}
 */
export function injectDefaultParamsInNavigationGuard(storeLocation) {
  const location = cloneDeep(storeLocation);
  const entity = location.params.entity;
  let modified = false;

  if (entity === 'all') {
    return { modified, location };
  }
  location.query = location.query || {};
  modified = addSiteIdFilterToRoute(location) || modified;
  modified = addDefaultLimitToRoute(location, entity) || modified;
  modified = addDefaultSortToRoute(location, entity) || modified;

  return { modified, location };
}

/**
 * Añade parámetros por defecto a las rutas del buscador.
 * Devuelve la ruta resultante después de haber añadido los parámetros por defecto.
 * @param {{}} storeLocation
 * @return {{}}
 */
export function injectDefaults(storeLocation) {
  const { location } = injectDefaultParamsInNavigationGuard(storeLocation);
  addTypeFilterToContentRoute(location, location.params.entity);

  return location;
}
